import { NavigationItem, Category } from "@/components/header/types";

/**
 * Checks if a URL is currently active based on pathname and search params
 */
export const isActive = (
  url: string,
  pathname: string,
  searchParams: URLSearchParams,
) => {
  if (!url) return false;
  const [baseUrl = "", query] = url.split("?");

  // Normalize base URLs by stripping trailing slashes for comparison
  const normalizedPathname = pathname.replace(/\/$/, "");
  const normalizedBaseUrl = baseUrl.replace(/\/$/, "");

  if (normalizedPathname !== normalizedBaseUrl) return false;

  const currentQueryCount = Array.from(searchParams.entries()).length;

  if (!query) {
    // A base URL with no query is only 'active' if no filters are applied
    // (We ignore 'id' if it's the only param, as it's often the base category id)
    if (currentQueryCount === 0) return true;
    if (currentQueryCount === 1 && searchParams.has("id")) return true;
    return false;
  }

  const params = new URLSearchParams(query);

  // Check if every parameter in the navigation link is present and matches (or is included in) the current URL
  for (const [key, value] of params.entries()) {
    const currentVal = searchParams.get(key);
    if (!currentVal) return false;

    // Split by comma for multi-select support
    const currentValues = currentVal.split(",");
    const navValues = value.split(",");

    // Every value in the navigation link must be present in the current search params
    const allMatch = navValues.every((nv) => currentValues.includes(nv));
    if (!allMatch) return false;
  }
  return true;
};

/**
 * Determines if a navigation item has any dropdown content
 */
export const hasDropdownContent = (item: NavigationItem) => {
  if (!item) return false;

  return item.subTypes && item.subTypes.length > 0;
};

/**
 * Maps a navigation item to a consistent category structure
 */
export const getCategories = (item: NavigationItem): Category[] => {
  const categories: Category[] = [];
  if (!item) return categories;

  // 1. SubTypes (Main Categories)
  if (item.subTypes && item.subTypes.length > 0) {
    const subTypeItems = item.subTypes.map((sub) => {
      const children = sub.children ? [...sub.children] : [];
      if (children.length > 0) {
        // Add "All [Heading]" link to children
        children.unshift({
          id: `all-${sub.id}`,
          heading: `All ${sub.heading}`,
          url: sub.url,
        });
      }
      return {
        id: sub.id,
        title: sub.heading,
        url: sub.url,
        children,
        content: sub.content || "",
      };
    });

    // Add "All [Category]" item at the beginning
    subTypeItems.unshift({
      id: `all-${item.id}`,
      title: `All ${item.title}`,
      url: item.url,
      children: [], // No nesting for the "All" link
      content: item.content || "",
    });

    categories.push({
      type: "subTypes",
      title: "", // Heading removed as requested
      items: subTypeItems,
      showAllUrl: item.url,
      hasMore: item.subTypes.length > 14,
    });
  }

  return categories;
};

/**
 * Gets standalone links that don't have nested items
 */
export const getStandaloneCategoryLinks = (item: NavigationItem) => {
  return [];
};

/**
 * Filter standalone top-level headers
 */
export const getStandaloneHeaders = (navigation: NavigationItem[]) => {
  return [];
};
